type ExpenseValue = {
  quantity?: number | null;
  amount?: number | null;
};

type ExpenseMap = Record<string, ExpenseValue | null | undefined>;

type LandmanEntry = {
  days?: number | null;
  total?: number | null;
  expenses?: ExpenseMap | null;
};

type LandmanBroker = {
  userId?: number | null;
  name?: string | null;
  businessName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  entries?: LandmanEntry[] | null;
  totals?: {
    days?: number | null;
    amountDue?: number | null;
  } | null;
};

type SummaryRow = {
  payeeId: number | null;
  payeeName: string;
  rowType?: "grand-total";
  days: number;
  laborAmount: number;
  expenses: ExpenseMap;
  totalExpenses: number;
  total: number;
};

type NormalizedLandmanBroker = LandmanBroker & {
  payeeName: string;
};

type NormalizedLandmanData<T> = Omit<T, "brokers"> & {
  brokers: NormalizedLandmanBroker[];
};

const numberValue = (value: unknown) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value: number) => Math.round(value * 100) / 100;

const trimValue = (value: unknown) => String(value ?? "").trim();

const normalizePayeeMergeKey = (value: string) =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

export function getLandmanPayeeName(broker: LandmanBroker | null | undefined) {
  const fullName = `${trimValue(broker?.firstName)} ${trimValue(broker?.lastName)}`.trim();
  return trimValue(broker?.businessName)
    || trimValue(broker?.name)
    || fullName
    || "Unknown Payee";
}

function getBrokerEntries(broker: LandmanBroker) {
  return Array.isArray(broker.entries) ? broker.entries : [];
}

function getEffectiveBrokerTotals(broker: LandmanBroker) {
  const entries = getBrokerEntries(broker);
  return {
    days: numberValue(broker.totals?.days ?? entries.reduce((sum, entry) => sum + numberValue(entry.days), 0)),
    amountDue: roundMoney(numberValue(broker.totals?.amountDue ?? entries.reduce((sum, entry) => sum + numberValue(entry.total), 0))),
  };
}

function aggregateExpenses(entries: LandmanEntry[]) {
  const expenses: ExpenseMap = {};

  for (const entry of entries) {
    const entryExpenses = entry.expenses || {};
    for (const [expenseTypeId, expense] of Object.entries(entryExpenses)) {
      const current = expenses[expenseTypeId] || { quantity: 0, amount: 0 };
      expenses[expenseTypeId] = {
        quantity: numberValue(current.quantity) + numberValue(expense?.quantity),
        amount: roundMoney(numberValue(current.amount) + numberValue(expense?.amount)),
      };
    }
  }

  return expenses;
}

function sumExpenseAmounts(expenses: ExpenseMap) {
  return roundMoney(
    Object.values(expenses).reduce((sum, expense) => sum + numberValue(expense?.amount), 0),
  );
}

function buildBrokerSummaryRow(broker: LandmanBroker): SummaryRow {
  const entries = getBrokerEntries(broker);
  const expenses = aggregateExpenses(entries);
  const totalExpenses = sumExpenseAmounts(expenses);
  const days = numberValue(broker.totals?.days ?? entries.reduce((sum, entry) => sum + numberValue(entry.days), 0));
  const entryTotal = entries.reduce((sum, entry) => sum + numberValue(entry.total), 0);
  const total = roundMoney(numberValue(broker.totals?.amountDue ?? entryTotal));

  return {
    payeeId: broker.userId ?? null,
    payeeName: getLandmanPayeeName(broker),
    days,
    laborAmount: roundMoney(total - totalExpenses),
    expenses,
    totalExpenses,
    total,
  };
}

function buildGrandTotalRow(rows: SummaryRow[]): SummaryRow {
  const expenses: ExpenseMap = {};

  for (const row of rows) {
    for (const [expenseTypeId, expense] of Object.entries(row.expenses || {})) {
      const current = expenses[expenseTypeId] || { quantity: 0, amount: 0 };
      expenses[expenseTypeId] = {
        quantity: numberValue(current.quantity) + numberValue(expense?.quantity),
        amount: roundMoney(numberValue(current.amount) + numberValue(expense?.amount)),
      };
    }
  }

  const total = roundMoney(rows.reduce((sum, row) => sum + numberValue(row.total), 0));
  const totalExpenses = sumExpenseAmounts(expenses);

  return {
    payeeId: null,
    payeeName: "Grand Total",
    rowType: "grand-total",
    days: rows.reduce((sum, row) => sum + numberValue(row.days), 0),
    laborAmount: roundMoney(total - totalExpenses),
    expenses,
    totalExpenses,
    total,
  };
}

export function normalizeLandmanPayees<T extends { brokers?: LandmanBroker[] | null }>(
  data: T,
): NormalizedLandmanData<T> {
  const brokers = Array.isArray(data?.brokers) ? data.brokers : [];
  const merged = new Map<string, NormalizedLandmanBroker>();

  brokers.forEach((broker, index) => {
    const payeeName = getLandmanPayeeName(broker);
    const normalizedName = normalizePayeeMergeKey(payeeName);
    const key = normalizedName && normalizedName !== "unknown payee"
      ? normalizedName
      : `unknown-${broker.userId ?? index}`;
    const entries = getBrokerEntries(broker);
    const totals = getEffectiveBrokerTotals(broker);
    const existing = merged.get(key);

    if (!existing) {
      merged.set(key, {
        ...broker,
        payeeName,
        entries: [...entries],
        totals,
      });
      return;
    }

    const existingTotals = getEffectiveBrokerTotals(existing);
    existing.entries = [...getBrokerEntries(existing), ...entries];
    existing.totals = {
      days: numberValue(existingTotals.days) + numberValue(totals.days),
      amountDue: roundMoney(numberValue(existingTotals.amountDue) + numberValue(totals.amountDue)),
    };
  });

  return {
    ...data,
    brokers: Array.from(merged.values()),
  } as NormalizedLandmanData<T>;
}

export function buildLandmanPaymentSummaryData<T extends { brokers?: LandmanBroker[] | null }>(data: T) {
  const normalized = normalizeLandmanPayees(data);
  const brokerRows = (normalized.brokers || []).map(buildBrokerSummaryRow);
  const totals: SummaryRow = brokerRows.length > 0 ? buildGrandTotalRow(brokerRows) : {
    payeeId: null,
    payeeName: "Grand Total",
    rowType: "grand-total",
    days: 0,
    laborAmount: 0,
    expenses: {},
    totalExpenses: 0,
    total: 0,
  };

  return {
    ...normalized,
    summaryRows: brokerRows,
    totals,
  };
}
